import { Fragment, useImperativeHandle, useLayoutEffect } from 'react';
import type { ComponentPropsWithRef, FC } from 'react';

export type BodyProps = ComponentPropsWithRef<'body'>;

const toKebabCase = (prop: string): string =>
    prop.startsWith('--') ? prop : prop.replace(/([A-Z])/g, '-$1').toLowerCase();

export const Body: FC<BodyProps> = ({ children, className, style, ref, ...props }) => {
    useImperativeHandle(ref, () => document.body as HTMLBodyElement);

    useLayoutEffect(() => {
        if (!className) return;
        document.body.classList.add(...className.split(' ').filter(Boolean));
        return () => document.body.classList.remove(...className.split(' ').filter(Boolean));
    }, [className]);

    useLayoutEffect(() => {
        if (!style) return;
        (Object.entries(style) as [string, string][]).forEach(([prop, value]) =>
            document.body.style.setProperty(toKebabCase(prop), value),
        );
        return () =>
            (Object.entries(style) as [string, string][]).forEach(([prop]) =>
                document.body.style.removeProperty(toKebabCase(prop)),
            );
    }, [style]);

    useLayoutEffect(() => {
        Object.entries(props)
            .filter(([key]) => key.startsWith('data-'))
            .forEach(([key, value]) => {
                if (value == null) {
                    document.body.removeAttribute(key);
                } else {
                    document.body.setAttribute(key, value as string);
                }
            });
        return () =>
            Object.entries(props)
                .filter(([key]) => key.startsWith('data-'))
                .forEach(([key]) => document.body.removeAttribute(key));
    });

    return <Fragment>{children}</Fragment>;
};
